import axiosInstance from './axiosInstance';

const leaveApi = {
  // ==================== EMPLOYEE LEAVE OPERATIONS ====================

  /**
   * Get all leaves for the logged-in employee
   * Backend: GET /api/leaves/my-leaves/
   * Response shape: { success, count, data: [...] }
   */
  getEmployeeLeaves: () => {
    return axiosInstance.get('/api/leaves/my-leaves/');
  },

  /**
   * Apply for a new leave
   * Backend: POST /api/leaves/
   * Response shape: { success, message, leave: {...}, approvers: {...} }
   *
   * @param {Object} leaveData - { employee_id, leave_type, planning_type, duration, from_date, to_date, reason }
   */
  applyLeave: (leaveData) => {
    return axiosInstance.post('/api/leaves/', leaveData);
  },

  /**
   * Get leave balance for the logged-in employee
   */
  getLeaveBalance: () => {
    return axiosInstance.get('/api/leaves/balance/');
  },

  /**
   * Get leave summary for the logged-in employee
   */
  getLeaveSummary: () => {
    return axiosInstance.get('/api/leaves/summary/');
  },

  // ==================== APPROVER OPERATIONS ====================

  /**
   * Get leaves pending MY approval (role-aware).
   * Backend: GET /api/leaves/pending/
   * Response shape: { success, count, approval_level, data: [...] }
   *
   * approval_level will be one of: "ADMIN" | "DEPARTMENT_HEAD" | "HR" | null
   */
  getPendingLeaves: () => {
    return axiosInstance.get('/api/leaves/pending/');
  },

  /**
   * Department Head approval — FIRST LEVEL
   * Backend: POST /api/leaves/{id}/dept-head-approve/
   *
   * @param {number} leaveId
   * @param {string} action  - 'approve' | 'reject'
   * @param {string} [remark]
   */
  deptHeadApproval: (leaveId, action, remark = '') => {
    return axiosInstance.post(`/api/leaves/${leaveId}/dept-head-approve/`, {
      action,
      remark,
    });
  },

  /**
   * HR approval — SECOND LEVEL (only callable after dept head approved)
   * Backend: POST /api/leaves/{id}/hr-approve/
   *
   * @param {number} leaveId
   * @param {string} action  - 'approve' | 'reject'
   * @param {string} [remark]
   */
  hrApproval: (leaveId, action, remark = '') => {
    return axiosInstance.post(`/api/leaves/${leaveId}/hr-approve/`, {
      action,
      remark,
    });
  },

  /**
   * Get detailed approval info for a single leave
   * Backend: GET /api/leaves/{id}/approval-info/
   */
  getApprovalInfo: (leaveId) => {
    return axiosInstance.get(`/api/leaves/${leaveId}/approval-info/`);
  },

  // ==================== HR / ADMIN LIST VIEW ====================

  /**
   * Get all leaves with optional filters (HR / admin view)
   * Backend: GET /api/leaves/?status=&employee_id=&leave_type=&from_date=&to_date=
   *
   * @param {Object} params
   * @param {string} [params.status]      - e.g. 'PENDING_DEPT_HEAD' | 'PENDING_HR' | 'APPROVED' | 'REJECTED_BY_DEPT_HEAD' | 'REJECTED_BY_HR'
   * @param {number} [params.employee_id]
   * @param {string} [params.leave_type]  - 'CASUAL' | 'MEDICAL' | 'EARNED'
   * @param {string} [params.from_date]   - YYYY-MM-DD
   * @param {string} [params.to_date]     - YYYY-MM-DD
   */
  getAllLeaves: (params = {}) => {
    return axiosInstance.get('/api/leaves/', { params });
  },

  // ==================== SINGLE LEAVE DETAIL ====================

  /**
   * Get a single leave record
   * Backend: GET /api/leaves/{id}/
   */
  getLeaveDetails: (leaveId) => {
    return axiosInstance.get(`/api/leaves/${leaveId}/`);
  },

  // ==================== REPORTS & STATISTICS ====================

  /**
   * Get leave report for a date range
   * @param {Object} params - { from_date, to_date, employee_id? }
   */
  getLeaveReport: (params) => {
    return axiosInstance.get('/api/leaves/report/', { params });
  },

  /**
   * Export leaves as CSV
   * @param {Object} params - same filters as getAllLeaves
   */
  exportLeavesCSV: (params = {}) => {
    return axiosInstance.get('/api/leaves/export/', {
      params,
      responseType: 'blob',
    });
  },

  /**
   * Get leave statistics
   * @param {Object} params - { period?: 'month'|'quarter'|'year', date? }
   */
  getLeaveStats: (params = {}) => {
    return axiosInstance.get('/api/leaves/stats/', { params });
  },

  // ==================== LEAVE TYPES & POLICY ====================

  getLeaveTypes: () => {
    return axiosInstance.get('/api/leaves/types/');
  },

  getLeavePolicy: () => {
    return axiosInstance.get('/api/leaves/policy/');
  },
};

export default leaveApi;
export { leaveApi };